import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from './AuthContext';
import { CourseIDContext } from './CourseIDContext';
//import debounce from 'lodash.debounce';

const StudentGradesContext = createContext();

const GRADES_UPDATE_STORAGE_KEY = 'myphilosophy:gradesUpdated';

const StudentGradesProvider = ({ children }) => {
  const [studentGradesState, setStudentGradesState] = useState({});
  const { courseID } = useContext(CourseIDContext);

  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  const authContext = useContext(AuthContext);  // Access AuthContext
  const { isLoggedIn, isInstructor, isTA, username: authUsername } = authContext;
  const [loadingGrades, setLoadingGrades] = useState(true); // New state for loading
  const storageRefreshTimeoutRef = useRef(null);
  const pendingUpdatesRef = useRef(new Set());
  const pendingFlushTimeoutRef = useRef(null);
  const postDebounceTimersRef = useRef({});

  const broadcastGradesUpdated = useCallback(() => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      const payload = JSON.stringify({ courseID, timestamp: Date.now() });
      window.localStorage.setItem(GRADES_UPDATE_STORAGE_KEY, payload);
    } catch (error) {
      console.warn('Unable to broadcast grades update event:', error);
    }
  }, [courseID]);

  const buildStateFromGrades = useCallback((gradesData, usernameOverride) => {
    if (!Array.isArray(gradesData)) {
      console.warn('Unexpected grades payload, keeping existing state.');
      return null;
    }

    return gradesData.reduce((acc, item) => {
      const targetUsername = usernameOverride || item.username;
      if (!acc[targetUsername]) {
        acc[targetUsername] = [];
      }
      acc[targetUsername].push({
        gradingType: item.gradingType,
        questionID: item.questionID || 'noQuestionID',
        individualGradingData: item.individualGradingData,
        comment: item.comment,
        evaluationDataID: item.evaluationDataID,
      });
      return acc;
    }, {});
  }, []);

  const fetchGrades = useCallback(async () => {
    if (!isLoggedIn) return; // no token yet — don't fire a pre-login request
    if (pendingUpdatesRef.current.size > 0) {
      return;
    }

    try {
      if (isInstructor || isTA) {
        const response = await axios.get(`${apiUrl}/api/studentGrades/${courseID}`);
        const nextState = buildStateFromGrades(response.data);
        if (nextState) {
          setStudentGradesState(nextState);
        }
      } else {
        const response = await axios.get(`${apiUrl}/api/studentGrades/${courseID}/${authUsername}`);
        const nextState = buildStateFromGrades(response.data, authUsername);
        if (nextState) {
          setStudentGradesState((prevState) => ({
            ...prevState,
            [authUsername]: nextState[authUsername] || [],
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching student grades data:', error);
    } finally {
      setLoadingGrades(false);
    }
  }, [apiUrl, authUsername, buildStateFromGrades, courseID, isInstructor, isLoggedIn, isTA]);

  // Fetch initial state from the backend on page load
  useEffect(() => {
    // Initial fetch on mount
    fetchGrades();

    const handleFocus = () => {
      fetchGrades();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchGrades();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchGrades]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleStorage = (event) => {
      if (event.key !== GRADES_UPDATE_STORAGE_KEY || !event.newValue) {
        return;
      }

      try {
        const parsed = JSON.parse(event.newValue);
        if (parsed.courseID !== courseID) {
          return;
        }
      } catch (error) {
        console.warn('Unable to parse grades update payload:', error);
        return;
      }

      if (storageRefreshTimeoutRef.current) {
        return;
      }

      storageRefreshTimeoutRef.current = window.setTimeout(() => {
        storageRefreshTimeoutRef.current = null;
        fetchGrades();
      }, 250);
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
      if (storageRefreshTimeoutRef.current) {
        clearTimeout(storageRefreshTimeoutRef.current);
        storageRefreshTimeoutRef.current = null;
      }
    };
  }, [courseID, fetchGrades]);

  // Function to handle changes in the state and database
  const handleGradeChange = (username, questionID, gradingType, individualGradingData, comment, evaluationDataID) => {
    const gradeData = {
      username,
      gradingType,
      questionID,
      individualGradingData,
      comment,
      evaluationDataID,
    };

    // Update state immutably
    setStudentGradesState((prevState) => {
      const newGrades = { ...prevState };

      if (!newGrades[username]) {
        newGrades[username] = [];
      } else {
        newGrades[username] = [...newGrades[username]];
      }

      const gradeIndex = newGrades[username].findIndex((g) => g.questionID === questionID);

      if (gradeIndex !== -1) {
        newGrades[username][gradeIndex] = {
          ...newGrades[username][gradeIndex],
          gradingType,
          individualGradingData,
          comment,
        };
      } else {
        newGrades[username].push(gradeData);
      }

      return newGrades;
    });

    // Debounce the POST per logical key so that rapid keystrokes (e.g. typing "10")
    // only send one request with the final value, preventing race conditions where
    // an earlier keystroke's response arrives after a later one and overwrites the DB.
    const logicalKey = `${username}:${questionID}:${evaluationDataID}:${gradingType}`;

    if (postDebounceTimersRef.current[logicalKey]) {
      clearTimeout(postDebounceTimersRef.current[logicalKey]);
    }

    postDebounceTimersRef.current[logicalKey] = setTimeout(() => {
      delete postDebounceTimersRef.current[logicalKey];

      const uniqueToken = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const pendingKey = `${logicalKey}:${uniqueToken}`;
      pendingUpdatesRef.current.add(pendingKey);

      axios
        .post(`${apiUrl}/api/studentGrades/update/${courseID}`, gradeData)
        .then(() => {
          pendingUpdatesRef.current.delete(pendingKey);
          broadcastGradesUpdated();
          if (pendingUpdatesRef.current.size === 0) {
            if (pendingFlushTimeoutRef.current) {
              clearTimeout(pendingFlushTimeoutRef.current);
            }
            pendingFlushTimeoutRef.current = window.setTimeout(() => {
              pendingFlushTimeoutRef.current = null;
              fetchGrades();
            }, 150);
          }
        })
        .catch((error) => {
          console.error('Error updating grade:', error);
          pendingUpdatesRef.current.delete(pendingKey);
        });
    }, 400);
  };

  const fetchGradesForStudent = async (username) => {
    try {
      const response = await axios.get(`${apiUrl}/api/studentGrades/${courseID}/${username}`);
      const nextState = buildStateFromGrades(response.data, username);
      if (nextState) {
        setStudentGradesState((prev) => ({
          ...prev,
          [username]: nextState[username] || [],
        }));
      }
    } catch (error) {
      console.error(`Error fetching grades for student ${username}:`, error);
    }
  };

  const deleteGrades = async (username, evaluationDataID, gradingType) => {
    try {
      const response = await axios.delete(`${apiUrl}/api/studentGrades/delete/${courseID}`, {
        data: { username, evaluationDataID, gradingType },
      });

      console.log('Grades deleted successfully:', response.data);

      setStudentGradesState((prevState) => {
        const updatedGrades = { ...prevState };

        if (updatedGrades[username]) {
          updatedGrades[username] = updatedGrades[username].filter(
            (grade) =>
              !(grade.evaluationDataID === evaluationDataID && grade.gradingType === gradingType)
          );
        }

        return updatedGrades;
      });

      broadcastGradesUpdated();
    } catch (error) {
      console.error('Error deleting grades:', error);
    }
  };

  return (
    <StudentGradesContext.Provider
      value={{
        studentGradesState,
        handleGradeChange,
        fetchGradesForStudent,
        deleteGrades,
        loadingGrades,
      }}
    >
      {children}
    </StudentGradesContext.Provider>
  );
};

export { StudentGradesContext, StudentGradesProvider };
